import React, { FC, useEffect } from "react";

import Link from "next/link";
import SkeletonBlock from "ui/Skeleton/components/SkeletonBlock/SkeletonBlock";
import LazyImage from "ui/LazyImage/LazyImage";
import Rating from "common/Rating/Rating";
import Price from "common/Price/Price";
import { useUrl } from "helpers/hooks";
import { formatDate } from "helpers/date";
import { cn } from "helpers/style";
import { capitalize } from "helpers/text";
import { getImageSrcWithSize } from "helpers/image";
import { IMAGE_SIZE_120_120 } from "constants/variables";
import styles from "./ProductInfo.module.scss";

type Props = {
  product: Entity.Product;
  onOpen?: () => void;
  type: "modal" | "tooltip";
};

const ProductInfo: FC<Props> = ({ product, onOpen, type }: Props) => {
  const { getEntityLinkProps } = useUrl();

  useEffect(() => {
    onOpen && onOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!product) {
    return (
      <div
        className={cn([
          styles.productInfo,
          styles[`productInfo${capitalize(type)}`],
        ])}
      >
        <div className={styles.productInfoImg}>
          <SkeletonBlock width="90px" height="90px" />
        </div>
        <div style={{ width: "100%" }}>
          <div style={{ display: "flex", marginBottom: "12px" }}>
            <SkeletonBlock
              width="100px"
              height="14px"
              style={{ marginRight: "5px" }}
            />
            <SkeletonBlock width="22px" height="14px" />
          </div>
          <SkeletonBlock
            width="209px"
            height="14px"
            style={{ marginBottom: "8px" }}
          />
          <SkeletonBlock
            width="72px"
            height="14px"
            style={{ marginBottom: "12px" }}
          />
          <SkeletonBlock
            width="124px"
            height="10px"
            style={{ marginBottom: "27px" }}
          />
          <SkeletonBlock width="72px" height="26px" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn([
        styles.productInfo,
        styles[`productInfo${capitalize(type)}`],
      ])}
    >
      <div className={styles.productInfoImg}>
        <LazyImage
          width={90}
          height={90}
          src={getImageSrcWithSize(product.image, IMAGE_SIZE_120_120)}
          alt={product.name}
        />
      </div>

      <div>
        {product.rating && (
          <div className={styles.productInfoRating}>
            <Rating value={product.rating} label={product.reviewsCount} />
          </div>
        )}

        <Link
          {...getEntityLinkProps("product", product)}
          className={styles.productInfoTitle}
        >
          {product.name}
        </Link>

        {product.expirationDate && (
          <div className={styles.productInfoExpirationDate}>
            Годен до {formatDate(product.expirationDate, "DD.MM.YYYY")}
          </div>
        )}

        <Price
          hasOnlyBookingPrices={product.hasOnlyBookingPrices}
          price={product.price}
          priceOld={product.priceOld}
          discount={product.discount}
        />
      </div>
    </div>
  );
};

export default ProductInfo;
